/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import ApiError from 'api/error'

import { useUserContext } from './user-context'

import type { FC, PropsWithChildren } from 'react'
import type { CustomerGraphicsConfig, CustomerProviderAuthConfig, StreamConfig } from 'types'

type SimpleRequestInit = Omit<RequestInit, 'method'>

const BASE_URL = `${import.meta.env.VITE_API_URL}/`
const GFX_BASE_URL = `${import.meta.env.VITE_API_GFX_URL}/`

const cleanUrl = (url: string) => BASE_URL + (url.startsWith('/') ? url.slice(1) : url)
const cleanGfxUrl = (url: string) => GFX_BASE_URL + (url.startsWith('/') ? url.slice(1) : url)

type ContextType = {
  isReady: boolean
  get: (url: string, init?: SimpleRequestInit) => Promise<any>
  post: (url: string, init?: SimpleRequestInit) => Promise<any>
  put: (url: string, init?: SimpleRequestInit) => Promise<any>
  del: (url: string, init?: SimpleRequestInit) => Promise<void>

  getGfx: (url: string, init?: SimpleRequestInit) => Promise<any>
  postGfx: (url: string, init?: SimpleRequestInit) => Promise<any>
  putGfx: (url: string, init?: SimpleRequestInit) => Promise<any>
  patchGfx: (url: string, init?: SimpleRequestInit) => Promise<any>
  delGfx: (url: string, init?: SimpleRequestInit) => Promise<void>

  // Unique functions for some legacy endpoints that are too difficult to generalise
  getGraphicsProfiles: (customerId: string) => Promise<any>
  getGraphicsCustomerConfig: (customerId: string) => Promise<any>
  putGraphicsCustomerConfig: (customerGraphicsConfig: CustomerGraphicsConfig) => Promise<any>
  putStream: (streamConfig: StreamConfig) => Promise<any>
  putAuthConfig: (url: string, authConfig: CustomerProviderAuthConfig) => Promise<any>
  getAuthHeader: () => any
}

export const ApiContext = createContext<ContextType>({} as ContextType)

export const ApiContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { session } = useUserContext()
  const [isReady, setIsReady] = useState(false)

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${session?.getAccessToken().getJwtToken()}` }), [session])
  const prepareHeaders = useCallback(
    (init?: SimpleRequestInit) =>
      init?.body instanceof FormData && ((init?.body as FormData).has('file') || (init?.body as FormData).has('files'))
        ? { ...init?.headers, ...authHeader }
        : { ...init?.headers, ...authHeader, 'Content-Type': 'application/json' },
    [authHeader]
  )

  useEffect(() => {
    if (Object.hasOwn(authHeader, 'Authorization')) setIsReady(true)
  }, [authHeader])

  // GENERICS

  const get = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      const response = await fetch(cleanUrl(url), {
        ...init,
        headers: prepareHeaders(init),
      })
      if (!response.ok) throw new ApiError(response.status)

      const data = await response.json()
      if (data.error) throw new Error(data.error)

      return data
    },
    [prepareHeaders]
  )

  const post = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      const response = await fetch(cleanUrl(url), {
        ...init,
        method: 'POST',
        headers: prepareHeaders(init),
        body: init?.body,
      })
      if (!response.ok) throw new ApiError(response.status)

      let data: any
      try {
        data = await response.json()
      } catch (err) {
        // If the response is empty, return nothing
        return
      }
      if (data.error) throw new Error(data.error)

      return data
    },
    [prepareHeaders]
  )

  const put = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      const response = await fetch(cleanUrl(url), {
        ...init,
        method: 'PUT',
        headers: prepareHeaders(init),
      })
      if (!response.ok) throw new ApiError(response.status)

      let data: any
      try {
        data = await response.json()
      } catch (err) {
        // If the response is empty, return nothing
        return
      }
      if (data.error) throw new Error(data.error)

      return data
    },
    [prepareHeaders]
  )

  const del = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      await fetch(cleanUrl(url), {
        ...init,
        method: 'DELETE',
        headers: prepareHeaders(init),
      })
    },
    [prepareHeaders]
  )

  const getGfx = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      const response = await fetch(cleanGfxUrl(url), {
        ...init,
        headers: prepareHeaders(init),
      })
      if (!response.ok) throw new ApiError(response.status)

      const data = await response.json()
      if (data.error) throw new Error(data.error)

      return data
    },
    [prepareHeaders]
  )

  const postGfx = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      const response = await fetch(cleanGfxUrl(url), {
        ...init,
        method: 'POST',
        headers: prepareHeaders(init),
        body: init?.body,
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.message)
      return result
    },
    [prepareHeaders]
  )

  const putGfx = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      const response = await fetch(cleanGfxUrl(url), {
        ...init,
        method: 'PUT',
        headers: prepareHeaders(init),
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.message)
      return result
    },
    [prepareHeaders]
  )

  const patchGfx = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      const response = await fetch(cleanGfxUrl(url), {
        ...init,
        method: 'PATCH',
        headers: prepareHeaders(init),
      })
      if (!response.ok) throw new ApiError(response.status)

      let data: any
      try {
        data = await response.json()
      } catch (err) {
        // If the response is empty, return nothing
        return
      }
      if (data.error) throw new Error(data.error)

      return data
    },
    [prepareHeaders]
  )

  const delGfx = useCallback(
    async (url: string, init?: SimpleRequestInit) => {
      await fetch(cleanGfxUrl(url), {
        ...init,
        method: 'DELETE',
        headers: prepareHeaders(init),
      })
    },
    [prepareHeaders]
  )

  // UNIQUE FUNCTIONS

  // Uses a different base URL
  const getGraphicsCustomerConfig = useCallback(
    async (customerId: string) => {
      const response = await fetch(`${import.meta.env.VITE_API_GFX_URL}/customer/${customerId}`, {
        headers: prepareHeaders(),
      })
      if (!response.ok) {
        const error = await response.json()
        // eslint-disable-next-line no-console
        console.error('Unable to get customer graphics config', error)
        return []
      }

      const data = await response.json()
      if (data.error) {
        throw new Error(data.error)
      }
      return data
    },
    [prepareHeaders]
  )

  const putGraphicsCustomerConfig = useCallback(
    async (customerGraphicsConfig: CustomerGraphicsConfig) => {
      const response = await fetch(
        `${import.meta.env.VITE_API_GFX_URL}/customer/${customerGraphicsConfig.customerUuid}`,
        {
          method: 'PUT',
          headers: prepareHeaders(),
          body: JSON.stringify(customerGraphicsConfig),
        }
      )
      if (!response.ok) {
        const error = await response.json()
        // eslint-disable-next-line no-console
        console.error('Unable to update customer config', error)
        return {
          httpResponseCode: response.status,
          httpStatusCode: response.status,
          outputMessage: error.outputMessage ?? error.title ?? null,
        }
      }

      const data = await response.json()
      if (data.error) {
        throw new Error(data.error)
      }
      return data
    },
    [prepareHeaders]
  )

  const getGraphicsProfiles = useCallback(
    async (customerId: string) => {
      const response = await fetch(`${import.meta.env.VITE_API_GFX_URL}/gfx/customer/${customerId}`, {
        headers: prepareHeaders(),
      })
      if (!response.ok) {
        const error = await response.json()
        // eslint-disable-next-line no-console
        console.error('Unable to get graphics profiles', error)
        return []
      }

      const data = await response.json()
      if (data.error) {
        throw new Error(data.error)
      }
      return data
    },
    [prepareHeaders]
  )

  // This is a legacy endpoint that doesn't really follow REST conventions
  // Ideally this should be broken up into both a POST for creation, and a PUT for updates, but would require backend changes to accommodate
  const putStream = useCallback(
    async (streamConfig: StreamConfig) => {
      const response = await fetch(cleanUrl('/control'), {
        method: 'PUT',
        headers: prepareHeaders(),
        body: JSON.stringify(streamConfig),
      })
      if (!response.ok) {
        const error = await response.json()
        // eslint-disable-next-line no-console
        console.error('Unable to schedule stream', error)
        return {
          httpResponseCode: response.status,
          httpStatusCode: response.status,
          outputMessage: error.outputMessage ?? error.title ?? null,
        }
      }

      const data = await response.json()
      if (data.error) {
        throw new Error(data.error)
      }
      return data
    },
    [prepareHeaders]
  )

  // This is pretty standard, but has weird error handling so can't use the generic PUT function
  const putAuthConfig = useCallback(
    async (url: string, authConfig: CustomerProviderAuthConfig) => {
      const response = await fetch(cleanUrl(url), {
        method: 'PUT',
        headers: prepareHeaders(),
        body: JSON.stringify(authConfig),
      })
      if (!response.ok) {
        const error = await response.json()
        // eslint-disable-next-line no-console
        console.error('Unable to update provider auth config', error)
        return {
          httpResponseCode: response.status,
          httpStatusCode: response.status,
          outputMessage: `Error: ${response.statusText}`,
        }
      }

      const data = await response.json()
      if (data.error) {
        throw new Error(data.error)
      }
      return data
    },
    [prepareHeaders]
  )

  const getAuthHeader = useCallback(() => authHeader, [authHeader])

  const contextValue = useMemo(
    () => ({
      isReady,
      get,
      post,
      put,
      del,
      getGfx,
      postGfx,
      putGfx,
      patchGfx,
      delGfx,
      getGraphicsProfiles,
      getGraphicsCustomerConfig,
      putGraphicsCustomerConfig,
      putStream,
      putAuthConfig,
      getAuthHeader,
    }),
    [
      isReady,
      get,
      post,
      put,
      del,
      getGfx,
      postGfx,
      putGfx,
      patchGfx,
      delGfx,
      getGraphicsProfiles,
      getGraphicsCustomerConfig,
      putGraphicsCustomerConfig,
      putStream,
      putAuthConfig,
      getAuthHeader,
    ]
  )

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>
}

export const useApi = () => {
  const context = useContext(ApiContext)
  if (!context) {
    throw new Error('useApi must be used within an ApiContextProvider')
  }
  return context
}
